// frontend\src\components\analytics\AnalyticsKpiCards.tsx
import React from 'react';
import { AnalyticsDataPoint, AnalyticsRevenueBasis } from '../../types';
import RevenueBasisInfo from './RevenueBasisInfo';

interface AnalyticsKpiCardsProps {
  data: AnalyticsDataPoint[];
  compareData?: AnalyticsDataPoint[];
  revenueBasis?: AnalyticsRevenueBasis;
}

const sumBy = (rows: AnalyticsDataPoint[], key: 'sales' | 'orderCount' | 'itemCount') =>
  rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const AnalyticsKpiCards: React.FC<AnalyticsKpiCardsProps> = ({ data, compareData, revenueBasis }) => {
  const revenue = sumBy(data, 'sales');
  const orders = sumBy(data, 'orderCount');
  const items = sumBy(data, 'itemCount');
  const aov = orders > 0 ? revenue / orders : 0;

  const hasCompare = !!compareData && compareData.length > 0;
  const compareRevenue = hasCompare ? sumBy(compareData!, 'sales') : 0;
  const compareOrders = hasCompare ? sumBy(compareData!, 'orderCount') : 0;
  const compareItems = hasCompare ? sumBy(compareData!, 'itemCount') : 0;
  const compareAov = compareOrders > 0 ? compareRevenue / compareOrders : 0;

  const percentChange = (current: number, previous: number): number | null =>
    previous > 0 ? ((current - previous) / previous) * 100 : null;

  // Best period: the bucket with the highest revenue in the current range
  const bestPeriod = data.reduce<AnalyticsDataPoint | null>(
    (best, row) => (!best || (Number(row.sales) || 0) > (Number(best.sales) || 0) ? row : best),
    null
  );

  const kpis: { label: string; value: string; change: number | null; sub?: string; info?: boolean }[] = [
    {
      label: 'Revenue',
      value: formatCurrency(revenue),
      change: percentChange(revenue, compareRevenue),
      info: true,
    },
    { label: 'Orders', value: formatNumber(orders), change: percentChange(orders, compareOrders) },
    { label: 'Items Sold', value: formatNumber(items), change: percentChange(items, compareItems) },
    { label: 'Avg Order Value', value: formatCurrency(aov), change: percentChange(aov, compareAov) },
    {
      label: 'Best Period',
      value: bestPeriod?.time || '—',
      change: null,
      sub: bestPeriod ? `${formatCurrency(Number(bestPeriod.sales) || 0)} in revenue` : undefined,
    },
  ];

  if (data.length === 0) return null;

  return (
    <div className="analytics-kpi-grid">
      {kpis.map((kpi) => (
        <div className="analytics-kpi-card" key={kpi.label}>
          <span className="analytics-kpi-label">
            {kpi.label}
            {kpi.info && <RevenueBasisInfo basis={revenueBasis} />}
          </span>
          <span className="analytics-kpi-value">{kpi.value}</span>
          {kpi.change !== null && (
            <span className={`analytics-kpi-delta ${kpi.change < 0 ? 'negative' : ''}`}>
              {kpi.change >= 0 ? '↑' : '↓'} {Math.abs(kpi.change).toFixed(1)}%
            </span>
          )}
          {kpi.sub && <span className="analytics-kpi-sub">{kpi.sub}</span>}
        </div>
      ))}
    </div>
  );
};

export default AnalyticsKpiCards;
